import { useState, useCallback, useEffect } from 'react';

// useStudyTimer Hook - Extracted from App.js for better maintainability
// Handles study session timing with auto-start functionality

const useStudyTimer = (selectedConcept, selectedDomain, searchTerm) => {
  const [studyStartTime, setStudyStartTime] = useState(null);
  const [studyTime, setStudyTime] = useState(0);
  const [isStudying, setIsStudying] = useState(false);

  // Study timer functionality
  const startStudySession = useCallback(() => {
    setStudyStartTime(Date.now());
    setIsStudying(true);
  }, []);

  const pauseStudySession = useCallback(() => {
    if (studyStartTime) {
      setStudyTime(prev => prev + (Date.now() - studyStartTime));
      setStudyStartTime(null);
    }
    setIsStudying(false);
  }, [studyStartTime]);

  const resetStudySession = useCallback(() => {
    setStudyTime(0);
    setStudyStartTime(null);
    setIsStudying(false);
  }, []);

  const getCurrentStudyTime = useCallback(() => {
    let currentTime = studyTime;
    if (isStudying && studyStartTime) {
      currentTime += (Date.now() - studyStartTime);
    }
    return currentTime;
  }, [studyTime, isStudying, studyStartTime]);

  const formatTime = useCallback((milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }, []);

  // Update study timer every second when studying
  useEffect(() => {
    let interval;
    if (isStudying) {
      interval = setInterval(() => {
        // Force re-render to update displayed time
        setStudyTime(prev => prev);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStudying, studyTime]);

  // Auto-start study session when user interacts with content
  useEffect(() => {
    if ((selectedConcept || selectedDomain || searchTerm) && !isStudying && studyTime === 0) {
      startStudySession();
    }
  }, [selectedConcept, selectedDomain, searchTerm, isStudying, studyTime, startStudySession]);

  return {
    studyTime,
    isStudying,
    startStudySession,
    pauseStudySession,
    resetStudySession,
    getCurrentStudyTime,
    formatTime
  };
};

export default useStudyTimer;